import React, {
  createContext,
  useCallback,
  useState,
  ReactNode,
  useEffect,
  useContext,
} from 'react'
import { MissionResponseData, SiteType } from '../../openapi'
import { UserContext } from '../user/provider'

export interface TutorialContextState {
  startInitialTutorial: boolean
  startDownloadTutorial: boolean
  /**
   * オープニング動画のモーダルがまだ閉じられていない。online 利用者の
   * チュートリアルミッションでは、オープニングが必ず出る。動画窓の Widget は
   * モーダルが閉じてから初期化する
   */
  isOpeningVideoPending: boolean
  setStartInitialTutorial: (start: boolean) => void
  setStartDownloadTutorial: (start: boolean) => void
  /** オープニング動画のモーダルを閉じたことを記録する */
  markOpeningVideoClosed: (missionId: number) => void
}

export const TutorialContext = createContext<TutorialContextState | undefined>(
  undefined,
)

interface TutorialProviderProps {
  children: ReactNode
  missionInfo?: MissionResponseData | undefined
  loading?: boolean
  isCopy?: boolean
  isTutorialMission?: boolean
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  children,
  missionInfo,
  loading = false,
  isCopy = false,
  isTutorialMission = false,
}) => {
  // isOnlineUser/isLicenseUser は siteName から別 useEffect で導出される派生
  // state のため、siteName 確定直後の描画では実際のサイトに関わらず一時的に
  // false のままになる。判定が確定するまでこの effect を進めないよう、
  // 派生 state ではなく siteName を直接見る。
  const { siteName } = useContext(UserContext)
  const isOnlineUser = siteName === SiteType.Online
  const [startInitialTutorial, setStartInitialTutorial] = useState(false)
  const [startDownloadTutorial, setStartDownloadTutorial] = useState(false)
  const [initialTutorialChecked, setInitialTutorialChecked] = useState(false)
  // オープニング動画のモーダルを閉じたミッション。ミッションごとに記録する
  const [openingVideoClosedMissionId, setOpeningVideoClosedMissionId] =
    useState<number | null>(null)

  useEffect(() => {
    if (!missionInfo || isCopy) {
      setInitialTutorialChecked(false)
      setStartInitialTutorial(false)
      setStartDownloadTutorial(false)
      // プロジェクトを読み直すと missionInfo がいったん undefined になり、同じ
      // ミッションを開き直すとオープニングがもう一度出る。閉じた記録も戻さないと
      // 待ちが解けたままになり、動画窓の Widget がモーダルの裏で先に初期化される
      setOpeningVideoClosedMissionId(null)
      return
    }
    if (!siteName) {
      return
    }
    if (!loading && !initialTutorialChecked) {
      let shouldStartInitialTutorial = false
      if (isTutorialMission) {
        if (isOnlineUser) {
          shouldStartInitialTutorial = true
        } else {
          shouldStartInitialTutorial =
            missionInfo?.chapters[0]?.currentChapterResult === null
        }
      }

      setInitialTutorialChecked(true)

      if (shouldStartInitialTutorial) {
        setStartInitialTutorial(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionInfo, loading, isTutorialMission, isCopy, siteName])

  const markOpeningVideoClosed = useCallback((missionId: number) => {
    setOpeningVideoClosedMissionId(missionId)
  }, [])

  // online 利用者のチュートリアルミッションではオープニングが必ず出る。
  // startInitialTutorial は effect で遅れて確定するため、動画窓の初期化がその前に
  // 走らないよう、同じ描画で判定できる値から導く（isOnlineUser は上のとおり
  // siteName から直接導いた値で、UserContext の派生 state ではない）
  const isOpeningVideoPending =
    isOnlineUser &&
    isTutorialMission &&
    missionInfo !== undefined &&
    openingVideoClosedMissionId !== missionInfo.id

  const value: TutorialContextState = {
    startInitialTutorial,
    startDownloadTutorial,
    isOpeningVideoPending,
    setStartInitialTutorial,
    setStartDownloadTutorial,
    markOpeningVideoClosed,
  }

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  )
}
